export type UblParty = {
  name: string | null;
  vatNumber: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
};

export type UblInvoiceLine = {
  lineId: string | null;
  description: string | null;
  quantity: string | null;
  unit: string | null;
  unitPrice: string | null;
  lineTotal: string | null;
  vatPercent: string | null;
};

export type ParsedUblInvoice = {
  documentType: 'Invoice' | 'CreditNote' | 'Unknown';
  invoiceNumber: string | null;
  issueDate: string | null;
  dueDate: string | null;
  currency: string | null;
  buyerReference: string | null;
  orderReference: string | null;
  paymentReference: string | null;
  note: string | null;
  supplier: UblParty | null;
  customer: UblParty | null;
  lines: UblInvoiceLine[];
  taxExclusiveAmount: string | null;
  taxAmount: string | null;
  taxInclusiveAmount: string | null;
  payableAmount: string | null;
  parseError: string | null;
};

const getLocalName = (element: Element) =>
  element.localName || element.tagName.split(':').pop() || '';

const textOf = (element: Element | null | undefined): string | null => {
  const value = element?.textContent?.trim();
  return value || null;
};

const findFirstByLocalName = (
  root: Document | Element,
  localName: string,
): Element | null => {
  const elements = root.getElementsByTagName('*');
  for (let i = 0; i < elements.length; i += 1) {
    if (getLocalName(elements[i]) === localName) {
      return elements[i];
    }
  }
  return null;
};

const findAllByLocalName = (root: Document | Element, localName: string): Element[] => {
  const results: Element[] = [];
  const elements = root.getElementsByTagName('*');
  for (let i = 0; i < elements.length; i += 1) {
    if (getLocalName(elements[i]) === localName) {
      results.push(elements[i]);
    }
  }
  return results;
};

const findChildByLocalName = (parent: Element, localName: string): Element | null => {
  for (let i = 0; i < parent.children.length; i += 1) {
    const child = parent.children[i];
    if (getLocalName(child) === localName) {
      return child;
    }
  }
  return null;
};

const findDescendantText = (root: Element | null, localName: string): string | null =>
  textOf(root ? findFirstByLocalName(root, localName) : null);

const parseParty = (partyContainer: Element | null): UblParty | null => {
  if (!partyContainer) {
    return null;
  }

  const party = findFirstByLocalName(partyContainer, 'Party') || partyContainer;
  const name =
    findDescendantText(party, 'RegistrationName')
    || findDescendantText(party, 'Name')
    || findDescendantText(findFirstByLocalName(party, 'PartyName'), 'Name');

  const addressEl = findFirstByLocalName(party, 'PostalAddress');
  const addressParts = addressEl
    ? [
        findDescendantText(addressEl, 'StreetName'),
        findDescendantText(addressEl, 'AdditionalStreetName'),
        [findDescendantText(addressEl, 'PostalZone'), findDescendantText(addressEl, 'CityName')]
          .filter(Boolean)
          .join(' '),
        findDescendantText(addressEl, 'CountrySubentity'),
        findDescendantText(findFirstByLocalName(addressEl, 'Country'), 'IdentificationCode'),
      ].filter(Boolean)
    : [];

  return {
    name,
    vatNumber:
      findDescendantText(party, 'CompanyID')
      || findDescendantText(findFirstByLocalName(party, 'PartyTaxScheme'), 'CompanyID'),
    address: addressParts.length > 0 ? addressParts.join(', ') : null,
    email: findDescendantText(findFirstByLocalName(party, 'Contact'), 'ElectronicMail'),
    phone: findDescendantText(findFirstByLocalName(party, 'Contact'), 'Telephone'),
  };
};

const parseInvoiceLine = (lineEl: Element): UblInvoiceLine => {
  const item = findFirstByLocalName(lineEl, 'Item');
  const price = findFirstByLocalName(lineEl, 'Price');
  const quantityEl =
    findChildByLocalName(lineEl, 'InvoicedQuantity')
    || findChildByLocalName(lineEl, 'CreditedQuantity');

  return {
    lineId: findDescendantText(lineEl, 'ID'),
    description:
      findDescendantText(item, 'Description')
      || findDescendantText(item, 'Name'),
    quantity: textOf(quantityEl),
    unit: quantityEl?.getAttribute('unitCode') || null,
    unitPrice:
      findDescendantText(price, 'PriceAmount')
      || findDescendantText(lineEl, 'PriceAmount'),
    lineTotal:
      findDescendantText(lineEl, 'LineExtensionAmount')
      || findDescendantText(lineEl, 'CreditLineAmount'),
    vatPercent: findDescendantText(
      findFirstByLocalName(lineEl, 'ClassifiedTaxCategory')
        || (item ? findFirstByLocalName(item, 'ClassifiedTaxCategory') : null),
      'Percent',
    ),
  };
};

const detectDocumentType = (doc: Document): ParsedUblInvoice['documentType'] => {
  const root = doc.documentElement;
  const rootName = getLocalName(root);
  if (rootName === 'Invoice') return 'Invoice';
  if (rootName === 'CreditNote') return 'CreditNote';
  return 'Unknown';
};

export const parseUblInvoice = (xmlContent: string): ParsedUblInvoice => {
  const empty: ParsedUblInvoice = {
    documentType: 'Unknown',
    invoiceNumber: null,
    issueDate: null,
    dueDate: null,
    currency: null,
    buyerReference: null,
    orderReference: null,
    paymentReference: null,
    note: null,
    supplier: null,
    customer: null,
    lines: [],
    taxExclusiveAmount: null,
    taxAmount: null,
    taxInclusiveAmount: null,
    payableAmount: null,
    parseError: null,
  };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlContent, 'text/xml');

    if (doc.querySelector('parsererror')) {
      return { ...empty, parseError: 'Kon UBL XML niet parsen.' };
    }

    const root = doc.documentElement;
    const documentType = detectDocumentType(doc);
    const monetaryTotal = findFirstByLocalName(root, 'LegalMonetaryTotal');
    const taxTotal = findFirstByLocalName(root, 'TaxTotal');
    const lines = findAllByLocalName(root, 'InvoiceLine').length
      ? findAllByLocalName(root, 'InvoiceLine')
      : findAllByLocalName(root, 'CreditNoteLine');

    const notes = findAllByLocalName(root, 'Note')
      .map(note => textOf(note))
      .filter(Boolean)
      .join('\n');

    return {
      documentType,
      invoiceNumber: findDescendantText(root, 'ID'),
      issueDate: findDescendantText(root, 'IssueDate'),
      dueDate:
        findDescendantText(root, 'DueDate')
        || findDescendantText(findFirstByLocalName(root, 'PaymentMeans'), 'PaymentDueDate'),
      currency:
        findDescendantText(root, 'DocumentCurrencyCode')
        || findDescendantText(root, 'TaxCurrencyCode'),
      buyerReference: findDescendantText(root, 'BuyerReference'),
      orderReference: findDescendantText(findFirstByLocalName(root, 'OrderReference'), 'ID'),
      paymentReference:
        findDescendantText(root, 'PaymentID')
        || findDescendantText(findFirstByLocalName(root, 'PaymentMeans'), 'PaymentID'),
      note: notes || null,
      supplier: parseParty(findFirstByLocalName(root, 'AccountingSupplierParty')),
      customer: parseParty(findFirstByLocalName(root, 'AccountingCustomerParty')),
      lines: lines.map(parseInvoiceLine),
      taxExclusiveAmount: findDescendantText(monetaryTotal, 'TaxExclusiveAmount'),
      taxAmount: findDescendantText(taxTotal, 'TaxAmount'),
      taxInclusiveAmount: findDescendantText(monetaryTotal, 'TaxInclusiveAmount'),
      payableAmount: findDescendantText(monetaryTotal, 'PayableAmount'),
      parseError: null,
    };
  } catch {
    return { ...empty, parseError: 'Kon UBL XML niet verwerken.' };
  }
};

export const formatUblAmount = (
  value: string | null | undefined,
  currency?: string | null,
) => {
  if (!value) return '—';
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return `${numeric.toFixed(2)} ${currency || 'EUR'}`;
  }
  return currency ? `${value} ${currency}` : value;
};
